import React from 'react';
// import { Prompt } from 'react-router';
import { Button } from '@mui/material';

import useBeforeUnload from '../hooks/useBeforeUnload';
import useSettings from '../hooks/useSettings';
import ResponsiveDialog from './ResponsiveDialog';

interface UnsavedDialogProps {
	/** If true, the value of the model has changed and this dialog could be shown */
	hasModelChanged: (() => boolean) | boolean;
	/** The name of the event that triggers the prompt */
	eventName?: string;
	/** The function called when the prompt is confirmed */
	onClose?: () => void;
}

/**
 *  This dialog show a localized version of the window.confirm browser popup (if supported by the browser vendor), shown only when the model has changed.
 *
 *  It's triggered by refreshing the page, by a page change caused of a url change and by the forward/backward buttons pressed.
 */
export default function UnsavedDialog({ hasModelChanged, eventName, onClose }: UnsavedDialogProps) {
	const { t } = useSettings();

	const [openDialog, setOpenDialog] = React.useState(false);

	const checkHasModelChanged = React.useCallback(
		() => (typeof hasModelChanged === 'function' ? hasModelChanged() : hasModelChanged),
		[hasModelChanged]
	);

	const onDialogClose = React.useCallback(
		(event: Event) => {
			if (checkHasModelChanged()) {
				setOpenDialog(true);
			} else {
				if (onClose) onClose();
			}
			event.stopPropagation();
		},
		[checkHasModelChanged, onClose]
	);

	React.useEffect(() => {
		if (eventName) {
			document.addEventListener(eventName, onDialogClose);
			return () => document.removeEventListener(eventName, onDialogClose);
		}

		return () => {};
	}, [eventName, onDialogClose]);

	useBeforeUnload(event => {
		if (import.meta.env.PROD) {
			if (checkHasModelChanged()) {
				event.preventDefault();
				if (event.defaultPrevented) {
					event.returnValue = '';
				}
			}
		}
	});

	return (
		<ResponsiveDialog
			open={openDialog}
			maxWidth="xs"
			title={t('PROMPT_TEXT_UNSAVED')}
			actions={
				<>
					<Button
						onClick={() => {
							setOpenDialog(false);
						}}
					>
						{t('DIALOG_ACTION_CANCEL')}
					</Button>
					<Button
						onClick={() => {
							setOpenDialog(false);
							if (onClose) onClose();
						}}
					>
						{t('DIALOG_ACTION_CONFIRM')}
					</Button>
				</>
			}
		/>
	);
}
